import { vec3 } from 'gl-matrix';

import { NavigationAwareObject } from '../NavigationAwareObject';
import { NavigationBehaviourBase } from './NavigationBehaviour';
import { RenderObject } from '../RenderObject';
import { PathNode } from '../../navigation-mesh';
import { sampleFootprintFloorHeight } from '../../spatial';
import { toRadian } from '../../math';

/** Reused per-call scratch for `agentClampT` so the sweep doesn't allocate. */
const AGENT_SWEEP_P1 = vec3.create();

export type PlayerInputState = {
  forward: boolean;
  backward: boolean;
  left: boolean;
  right: boolean;
  running: boolean;
};

export type PlayerControlConfig = {
  walkSpeed: number;
  runSpeed: number;
  getCameraYaw: () => number;
  getInputState: () => PlayerInputState;
};

export class PlayerControlBehaviour extends NavigationBehaviourBase {
  private navObject?: NavigationAwareObject;
  private lastFrameTime: number = 0;
  private moving = false;
  private currentNode?: PathNode;

  constructor(
    private entity: RenderObject,
    private config: PlayerControlConfig,
  ) {
    super();
  }

  attach(navObject: NavigationAwareObject) {
    this.navObject = navObject;
    navObject.movement.clear();
    this.currentNode = navObject.movement.currentNode?.node;
    this.lastFrameTime = performance.now();
    this.moving = false;
  }

  detach(navObject: NavigationAwareObject) {
    if (this.moving) {
      this.setMoveAnimation(this.entity, 'Idle');
    }
    this.navObject = undefined;
    this.currentNode = undefined;
    this.moving = false;
  }

  /**
   * Find which node contains a given XZ position by checking the current node
   * first, then its neighbours (graph walk). This avoids false boundaries from
   * global spatial queries and naturally follows the nav mesh connectivity
   * (including stairs and multi-level areas).
   */
  private findNodeAt(x: number, z: number): PathNode | undefined {
    if (!this.currentNode) return undefined;
    const currentY = (this.entity.position as vec3)[1];

    // Check current node first (no Y filter — player is already here)
    if (this.nodeContainsXZ(this.currentNode, x, z)) {
      return this.currentNode;
    }

    // Check immediate neighbours with Y-proximity guard
    for (const neighbour of this.currentNode.neighbours) {
      if (this.nodeContainsXZ(neighbour, x, z) && this.nodeIsHeightReachable(neighbour, x, z, currentY)) {
        return neighbour;
      }
    }

    // Check neighbours-of-neighbours (handles crossing small nodes like
    // stair treads or narrow corridors in a single frame)
    for (const neighbour of this.currentNode.neighbours) {
      for (const nn of neighbour.neighbours) {
        if (nn !== this.currentNode && this.nodeContainsXZ(nn, x, z) && this.nodeIsHeightReachable(nn, x, z, currentY)) {
          return nn;
        }
      }
    }

    return undefined;
  }

  private nodeContainsXZ(node: PathNode, x: number, z: number): boolean {
    const bb = node.getBoundingBox();
    return x >= bb.min[0] && x <= bb.max[0] && z >= bb.min[2] && z <= bb.max[2];
  }

  /**
   * Returns true if the actual floor at (x, z) inside `node` is reachable from
   * the player's current Y. Queries the spatial grid for the real surface height
   * rather than relying on `node.min[1]`, which is only the lowest corner of the
   * entire node polygon and gives false negatives on large sloped terrain nodes.
   * Falls back to `node.min[1]` when the spatial grid is unavailable (unit tests).
   *
   * Query Y is capped at `currentY + maxStep` rather than `node.max[1]`. Using
   * `node.max[1]` (top of the entire path-node AABB) lets the downward ray
   * hit obstacle tops inside the node — Sponza atrium columns are 4m tall
   * and live within a single ground-floor nav node, so a ray from `node.max[1]`
   * at a column-edge XZ returns the column top as "floor", which then fails
   * the step check and traps the player in their current node. Capping at
   * `currentY + maxStep` means we only consider floors that are actually
   * within step range — the ray starts in the open span above the player,
   * passes through any hollow obstacles, and lands on real walkable surfaces.
   * Genuinely-unreachable ledges higher than `currentY + maxStep` are above
   * the query origin, so the ray misses and the check correctly falls back
   * to `node.min[1]`.
   */
  private nodeIsHeightReachable(node: PathNode, x: number, z: number, currentY: number): boolean {
    const maxStep = this.navObject?.navigationData.navigation.maxNeighbourStepHeight ?? Infinity;
    const grid = this.navObject?.navigationData.spatialGrid;

    if (grid) {
      // For terrain-only areas with no volgrid tile, the navmesh itself guarantees
      // reachability — maxNeighbourStepHeight is an indoor step-ledge limit and
      // must not gate movement on open-world terrain slopes.
      if (!grid.hasTileAt(x, z)) {
        return true;
      }
      // Footprint sample (vs. single ray) so a seam between two flush-but-not-
      // welded floor tiles doesn't make the reachability check evaluate against
      // the floor below — `delta <= maxStep` accepts any downward drop and
      // would otherwise green-light moving onto a lower-floor node.
      const radius = this.navObject?.navigationData.selfAgent?.radius ?? 0;
      const floorY = sampleFootprintFloorHeight(grid, x, currentY, z, radius, maxStep);
      if (floorY !== null) {
        const delta = floorY - currentY;
        // Stepping UP is limited by maxStep (you can't climb a ledge taller
        // than half a meter). Stepping DOWN is unconditional — gravity will
        // catch the player. The previous symmetric check trapped the player
        // on raised features like Sponza atrium pedestals, where the neighbour
        // node is at the lower atrium floor (≥1m drop) and the symmetric
        // check rejected it as "unreachable".
        return delta <= maxStep;
      }
    }

    const nodeFloor = node.min[1] as number;
    const delta = nodeFloor - currentY;
    return delta <= maxStep;
  }

  private applyMove(
    position: vec3,
    newX: number,
    newZ: number,
    node: PathNode,
    direction: vec3,
    running: boolean,
  ) {
    position[0] = newX;
    position[2] = newZ;

    const grid = this.navObject!.navigationData.spatialGrid;
    const maxStep = this.navObject?.navigationData.navigation.maxNeighbourStepHeight ?? Infinity;
    const currentY = position[1] as number;
    // No real volgrid tile here — terrain fallback covers the floor query.
    // Skip the upward-snap limit so the player tracks the terrain surface on slopes.
    const noTile = !!grid && !grid.hasTileAt(position[0] as number, position[2] as number);

    const radius = this.navObject?.navigationData.selfAgent?.radius ?? 0;
    const surfaceHeight = grid
      ? sampleFootprintFloorHeight(
          grid,
          position[0] as number,
          currentY,
          position[2] as number,
          radius,
          maxStep,
        )
      : null;
    if (surfaceHeight !== null && surfaceHeight !== undefined) {
      const upwardSnap = surfaceHeight - currentY;
      if (noTile || upwardSnap <= maxStep) {
        const body = this.navObject!.physicsBody;
        if (body) {
          // While grounded, track the surface at the current XZ normally so
          // the player can walk onto a lower floor. While ungrounded, only
          // RAISE — the helper's pick can drift as XZ moves through a fall
          // trajectory, and a lower pick would drop the floor clamp beneath
          // a surface the body radius is still reaching, tunneling the
          // player past it. Holding the highest surface seen during the
          // fall pins them on it when they reach that Y.
          const existing = body.surfaceHeight;
          if (body.grounded || existing === null || surfaceHeight >= existing) {
            body.surfaceHeight = surfaceHeight;
            body.minFloorHeight = surfaceHeight;
          }
        } else {
          position[1] = surfaceHeight;
        }
      }
    }

    this.currentNode = node;
    this.navObject!.movement.setCurrentNode({
      position: vec3.clone(position),
      node,
    });

    this.entity.rotateTo(direction);
    this.setMoveAnimation(this.entity, running ? 'Run' : 'Walk');
    this.moving = true;
  }

  step() {
    if (!this.navObject) return;

    const deltaTime = this.advanceFrame();
    if (deltaTime === null) return;

    this.syncWithPathfinder();

    const input = this.config.getInputState();
    const direction = this.readInputDirection(input);
    if (!direction) {
      this.goIdle();
      return;
    }

    const speed = input.running ? this.config.runSpeed : this.config.walkSpeed;
    if (!this.tryMove(direction, speed * deltaTime, input.running)) {
      this.goIdle();
    }
  }

  /**
   * Advance the frame timer and return the delta in seconds. Returns `null`
   * when the delta is non-positive or large enough to indicate a stall (paused
   * tab, breakpoint), so the caller skips the frame entirely.
   */
  private advanceFrame(): number | null {
    const now = performance.now();
    const deltaTime = (now - this.lastFrameTime) / 1000;
    this.lastFrameTime = now;
    if (deltaTime <= 0 || deltaTime > 0.5) return null;
    return deltaTime;
  }

  /**
   * Reset any in-flight pathfinder movement and re-sync `currentNode` from
   * whatever the pathfinder left us on. The player controller and the
   * pathfinder both write to the entity's position, so this keeps our local
   * graph cursor consistent before reading input.
   */
  private syncWithPathfinder(): void {
    if (this.navObject!.movement.isActive) {
      this.navObject!.movement.clear();
    }
    if (this.navObject!.movement.currentNode?.node) {
      this.currentNode = this.navObject!.movement.currentNode.node;
    }
    // Re-acquire a node when we don't have one — this handles two cases:
    // (a) player spawned before navmesh tiles finished loading async,
    // (b) player walked to the edge of the loaded ring and new tiles just arrived.
    if (!this.currentNode) {
      this.currentNode = this.navObject!.getPathNode(this.entity.position, 10);
    }
  }

  /**
   * Convert the raw input + camera yaw into a normalized world-space movement
   * direction on the XZ plane. Returns `null` when there is no input, signaling
   * that the entity should transition to idle.
   */
  private readInputDirection(input: PlayerInputState): vec3 | null {
    const inputForward = (input.forward ? 1 : 0) - (input.backward ? 1 : 0);
    const inputRight = (input.right ? 1 : 0) - (input.left ? 1 : 0);
    if (inputForward === 0 && inputRight === 0) return null;

    const yawRad = toRadian(this.config.getCameraYaw());
    const sinYaw = Math.sin(yawRad);
    const cosYaw = Math.cos(yawRad);

    // Forward = [sin(yaw), 0, cos(yaw)]
    // Right = cross(forward, up) = [-cos(yaw), 0, sin(yaw)]
    const direction = vec3.fromValues(
      sinYaw * inputForward - cosYaw * inputRight,
      0,
      cosYaw * inputForward + sinYaw * inputRight,
    );
    vec3.normalize(direction, direction);
    return direction;
  }

  /**
   * Try to move the entity by `distance` along `direction`. Falls back to
   * sliding along each axis when the direct move is blocked. Returns `false`
   * if the entity is fully blocked and didn't move.
   *
   * Each candidate move also runs a hip-height `rayCrossesSolid` check against
   * the volume grid, BUT only when the target stays inside the player's
   * current nav node. The navmesh path nodes are coarser than scatter-object
   * footprints (a tree trunk often sits inside a single node's XZ AABB, with
   * no node-graph subdivision around it), so the per-node-graph check alone
   * lets the player walk through small obstacles — that's what the wall-cast
   * is for. When the target is in a NEIGHBOUR node, the navmesh has already
   * vetted that crossing as valid (the connectivity edge means the two nodes
   * are mutually traversable), and a literal line-of-sight from current to
   * target may legitimately pass through a column or wall that the player
   * walks AROUND via the node geometry — so skip the wall-cast across nodes
   * to avoid over-blocking. Single-ray is an approximation of true capsule
   * collision; if thin obstacles the ray skims past start mattering, bump to
   * a multi-ray sweep here.
   */
  private tryMove(direction: vec3, distance: number, running: boolean): boolean {
    const position = this.entity.position as vec3;
    const newX = position[0] + direction[0] * distance;
    const newZ = position[2] + direction[2] * distance;

    // Validate against nav mesh using graph walk (current node + neighbours).
    // For each candidate branch, `clampedTarget` runs the wall-cast in
    // distance mode: if the segment crosses solid geometry, the move is
    // clamped to just-before-contact instead of rejected outright. That lets
    // the player walk up to a wall and stop touching it, rather than being
    // held back at the wall-cast threshold and never approaching the surface.
    const direct = this.findNodeAt(newX, newZ);
    if (direct) {
      const clamped = this.clampedTarget(direct, position, newX, newZ);
      if (clamped) {
        if (clamped.moved) {
          this.applyMove(position, clamped.x, clamped.z, direct, direction, running);
          return true;
        }
        // Hit at t=0 (in contact with wall already). Fall through to slides.
      }
    }

    // Blocked — try sliding along each axis
    const slideX = this.findNodeAt(newX, position[2]);
    if (slideX) {
      const clamped = this.clampedTarget(slideX, position, newX, position[2]);
      if (clamped && clamped.moved) {
        this.applyMove(position, clamped.x, clamped.z, slideX, direction, running);
        return true;
      }
    }

    const slideZ = this.findNodeAt(position[0], newZ);
    if (slideZ) {
      const clamped = this.clampedTarget(slideZ, position, position[0], newZ);
      if (clamped && clamped.moved) {
        this.applyMove(position, clamped.x, clamped.z, slideZ, direction, running);
        return true;
      }
    }

    return false;
  }

  /**
   * Combined gate for a candidate move: if the target is a NEIGHBOUR node
   * (cross-node move), the navmesh has already vetted it — accept without
   * wall-cast. If the target is the SAME node (in-node move), run the
   * hip-height wall-cast to catch sub-node obstacles like tree trunks.
   */
  private canCrossTo(target: PathNode, position: vec3, targetX: number, targetZ: number): boolean {
    if (target !== this.currentNode) return true;
    return !this.blockedByObstacle(position, targetX, targetZ);
  }

  /**
   * Wall-cast in distance mode. For cross-node targets, always returns the
   * full target (navmesh has vetted the crossing). For in-node targets, casts
   * a hip-height segment from current position to target. If the cast hits
   * solid geometry at t ∈ [0, 1), clamps the target to a fraction CLAMP_PADDING
   * before the hit point so the player doesn't overlap the wall.
   *
   * Returns `null` for degenerate zero-length segments (no movement requested).
   * Returns `moved: false` when the hit is at t=0 (player is already touching
   * the wall, no forward motion possible).
   */
  private clampedTarget(
    target: PathNode,
    position: vec3,
    targetX: number,
    targetZ: number,
  ): { x: number; z: number; moved: boolean } | null {
    const dx = targetX - position[0];
    const dz = targetZ - position[2];
    if (dx === 0 && dz === 0) return null;

    // Two clamps composed: walls (static geometry) and agents (other moving
    // entities). The agent sweep applies for both same-node AND cross-node
    // moves — agents can be anywhere, the navmesh doesn't route around them.
    const wallT = this.wallClampT(target, position, targetX, targetZ);
    const agentT = this.agentClampT(position, targetX, targetZ);
    const t = Math.min(wallT, agentT);
    if (t <= 0) {
      return { x: position[0], z: position[2], moved: false };
    }
    return {
      x: position[0] + dx * t,
      z: position[2] + dz * t,
      moved: true,
    };
  }

  /**
   * Resolve the wall-cast clamp `t` for a candidate move. Returns 1 for
   * cross-node moves (navmesh has vetted the crossing) and for in-node
   * moves that hit nothing. Hit-then-pad logic mirrors what the old single-
   * concern `clampedTarget` did before agents joined.
   */
  private wallClampT(
    target: PathNode,
    position: vec3,
    targetX: number,
    targetZ: number,
  ): number {
    if (target !== this.currentNode) return 1;
    const grid = this.navObject?.navigationData.spatialGrid;
    if (!grid || typeof grid.segmentHitDistance !== 'function') {
      return this.blockedByObstacle(position, targetX, targetZ) ? 0 : 1;
    }
    const hipY = position[1] + 0.5;
    const t = grid.segmentHitDistance(position[0], hipY, position[2], targetX, hipY, targetZ);
    if (t === null) return 1;
    const dx = targetX - position[0];
    const dz = targetZ - position[2];
    const CLAMP_PADDING = 0.05;
    const segLen = Math.sqrt(dx * dx + dz * dz);
    const padT = segLen > 1e-6 ? CLAMP_PADDING / segLen : 0;
    return Math.max(0, t - padT);
  }

  /**
   * Sweep the player's body capsule against every other registered agent
   * (NPCs, other players) and return the clamp `t` along the candidate
   * segment. 1 = no agent in the way. Skipped entirely when the scene
   * doesn't track agents or this entity isn't registered as one.
   */
  private agentClampT(position: vec3, targetX: number, targetZ: number): number {
    const navData = this.navObject?.navigationData;
    const reg = navData?.agentRegistry;
    const self = navData?.selfAgent;
    if (!reg || !self) return 1;
    AGENT_SWEEP_P1[0] = targetX;
    AGENT_SWEEP_P1[1] = position[1];
    AGENT_SWEEP_P1[2] = targetZ;
    const hit = reg.sweepHorizontal(position, AGENT_SWEEP_P1, self.radius, self);
    if (hit === null) return 1;
    // Same pad-back trick used for walls: don't sit inside the other agent's
    // surface, leave a small gap.
    const dx = targetX - position[0];
    const dz = targetZ - position[2];
    const CLAMP_PADDING = 0.05;
    const segLen = Math.sqrt(dx * dx + dz * dz);
    const padT = segLen > 1e-6 ? CLAMP_PADDING / segLen : 0;
    return Math.max(0, hit.t - padT);
  }

  /**
   * Single-ray wall-cast at hip height between the player's current position
   * and a candidate target. Picks up obstacles (tree trunks, fences, posts)
   * sitting inside a single path node's XZ AABB that the navmesh graph alone
   * doesn't subdivide around. Skipped when no volume grid is attached (unit
   * tests, headless contexts) or when the segment is degenerate.
   */
  private blockedByObstacle(position: vec3, targetX: number, targetZ: number): boolean {
    const grid = this.navObject?.navigationData.spatialGrid;
    // Mocks in unit tests provide a partial SpatialQueryGrid (no rayCrossesSolid);
    // treat missing method same as missing grid — don't block.
    if (!grid || typeof grid.rayCrossesSolid !== 'function') return false;
    const dx = targetX - position[0];
    const dz = targetZ - position[2];
    if (dx === 0 && dz === 0) return false;
    // Hip-height offset keeps the ray clear of the terrain plane (which would
    // otherwise cause false positives on the terrain BLAS) and through the
    // trunk's actual cross-section in BVH mode. In volgrid mode the trunk
    // voxels span the same Y range so the ray still hits.
    const hipY = position[1] + 0.5;
    return grid.rayCrossesSolid(position[0], hipY, position[2], targetX, hipY, targetZ);
  }

  private goIdle(): void {
    if (this.moving) {
      this.moving = false;
      this.setMoveAnimation(this.entity, 'Idle');
    }
  }
}
